import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Mail, Plus, RotateCcw, Trash2, UserPlus } from "lucide-react";
import {
  useCreateOrganization,
  useDeleteOrganization,
  useMyOrganizations,
  useLeaveOrganization,
  useResubmitOrganization,
  useSetRelationship,
  useUpdateOrganization,
  useRemoveOrganizationImage,
  useSetOrganizationImage,
} from "@/features/members/hooks/useOrganizations";
import {
  useMyInvites,
  useRespondToInvite,
  useSendInvite,
  type AcceptedInvite,
  type LegacyInvite,
} from "@/features/members/hooks/useInvites";
import type { OrganizationDetail } from "@/features/members/members.types";
import { clerkEnabled } from "@/services/clerk";
import { OrganizationPicker } from "@/components/OrganizationPicker";
import { ChoiceChips } from "@/components/ChoiceChips";
import { useInviteRelationship } from "@/components/InviteRelationship";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ImageUpload } from "@/components/ImageUpload";
import { LegacyCard } from "@/components/LegacyCard";
import { LegacyMark } from "@/components/LegacyMark";
import { usePublicConfig } from "@/features/applications/hooks/useApplications";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";

/**
 * The legacies you steward: the cards the directory shows, with everything
 * only a steward can do underneath them.
 *
 * An estate or foundation is an entity in its own right, so this is where one
 * comes into being — filled in, from its own form — and where it is corrected,
 * shared with a colleague, resubmitted after a decline, or removed. The
 * profile no longer carries a copy of the list: two places to answer one
 * question is how a member ends up stewarding something they do not.
 */
export default function MyOrganizationsPage() {
  const { data, isPending, error } = useMyOrganizations();
  const invites = useMyInvites(clerkEnabled);

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <h1 className="text-2xl">My organizations</h1>
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const organizations = data?.organizations ?? [];
  const received = invites.data?.received ?? [];
  const sent = invites.data?.sent ?? [];

  return (
    <div className="max-w-3xl space-y-4">
      {/* Its own heading, like every other page: the layout no longer renders
          one, because on the admin pages that produced two. */}
      <div>
        <h1 className="text-2xl">My organizations</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          The estates, foundations and bodies of work you look after. Each one
          is a directory entry in its own right, shared with whoever else
          stewards it.
        </p>
      </div>
      {error && <p className="text-destructive text-sm">{error.message}</p>}

      <InviteInbox invites={received} />
      <TokenFromEmail />

      {organizations.length === 0 && received.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              You don&apos;t steward any organizations yet
            </CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground space-y-2 text-sm">
            <p>
              Add the estate, foundation or body of work you look after and an
              administrator will review the entry.
            </p>
            <p>
              If it is already in the directory, one of its stewards can invite
              you to it, or an administrator can add you. That is the only way
              onto an entry somebody else filed.
            </p>
          </CardContent>
        </Card>
      )}

      {organizations.map((organization, index) => (
        <LegacyRow
          key={organization.id}
          organization={organization}
          index={index}
          invites={sent.filter((i) => i.organization.id === organization.id)}
        />
      ))}

      <NewOrganization />
    </div>
  );
}

/** One legacy: the directory's own card, with a steward's actions under it. */
function LegacyRow({
  organization,
  index,
  invites,
}: {
  organization: OrganizationDetail;
  index: number;
  invites: LegacyInvite[];
}) {
  const [panel, setPanel] = useState<"none" | "edit" | "invite">("none");
  const resubmit = useResubmitOrganization();
  const remove = useDeleteOrganization();
  const leave = useLeaveOrganization();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  const pending = organization.status === "pending";
  const declined = organization.status === "declined";
  // Sharing one means unlinking, not deleting: the row belongs to everybody on
  // it. `stewardCount` counts every active steward, including any the viewer
  // cannot see listed.
  const sole = (organization.stewardCount ?? 1) <= 1;

  return (
    <LegacyCard
      legacy={organization}
      index={index}
      footer={
        <div className="space-y-3">
          {pending && (
            <p className="text-muted-foreground text-xs">
              <Badge variant="secondary" className="mr-2">
                Awaiting review
              </Badge>
              It joins the directory once an administrator approves it. You can
              keep editing it meanwhile.
            </p>
          )}
          {declined && (
            <div className="border-destructive/40 space-y-1 rounded-md border px-2.5 py-2">
              <p className="text-sm font-medium">Declined</p>
              {organization.reviewNote ? (
                <p className="text-muted-foreground text-xs">
                  {organization.reviewNote}
                </p>
              ) : (
                <p className="text-muted-foreground text-xs">
                  An administrator declined this entry. The reason was emailed
                  to whoever filed it.
                </p>
              )}
              <p className="text-muted-foreground text-xs">
                Put it right and ask again. It goes back in the queue.
              </p>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={panel === "edit" ? "secondary" : "outline"}
              onClick={() => setPanel(panel === "edit" ? "none" : "edit")}
            >
              {panel === "edit" ? "Done" : "Edit"}
            </Button>
            <Button
              size="sm"
              variant={panel === "invite" ? "secondary" : "outline"}
              onClick={() => setPanel(panel === "invite" ? "none" : "invite")}
            >
              <UserPlus data-slot="icon" />
              Invite a steward
              {invites.length > 0 && (
                <span className="text-muted-foreground"> ({invites.length})</span>
              )}
            </Button>
            {declined && (
              <Button
                size="sm"
                disabled={resubmit.isPending}
                onClick={() =>
                  resubmit.mutate(organization.id, {
                    onSuccess: () => toast.success("Back in the review queue"),
                    onError: (err) => toast.error(err.message),
                  })
                }
              >
                <RotateCcw data-slot="icon" /> Ask again
              </Button>
            )}
            {/* Deleting is the last steward's; everybody else takes
                themselves off and leaves the row standing. */}
            {sole ? (
              <Button
                size="sm"
                variant="ghost"
                className="text-destructive hover:text-destructive"
                onClick={() => setConfirmingDelete(true)}
              >
                <Trash2 data-slot="icon" /> Delete
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground"
                onClick={() => setConfirmingLeave(true)}
              >
                <LogOut data-slot="icon" /> Leave
              </Button>
            )}
          </div>

          <Relationship organization={organization} />

          {panel === "edit" && <OrganizationEditor organization={organization} />}
          {panel === "invite" && (
            <InviteForm organization={organization} invites={invites} />
          )}

          <ConfirmDialog
            open={confirmingLeave}
            onOpenChange={setConfirmingLeave}
            title={`Leave ${organization.name}?`}
            description="You come off the entry and it stays with its other stewards. You can be invited back."
            confirmLabel="Leave"
            busy={leave.isPending}
            onConfirm={() => {
              setConfirmingLeave(false);
              // The server refuses once and explains when a legacy is what
              // covers your membership; confirming is answering that.
              leave.mutate(
                { id: organization.id },
                {
                  onSuccess: () => toast.success(`You have left ${organization.name}`),
                  onError: (err) =>
                    toast.error(err.message, {
                      action: {
                        label: "Leave anyway",
                        onClick: () =>
                          leave.mutate(
                            { id: organization.id, confirm: true },
                            {
                              onSuccess: () =>
                                toast.success(`You have left ${organization.name}`),
                              onError: (e) => toast.error(e.message),
                            }
                          ),
                      },
                    }),
                }
              );
            }}
          />

          <ConfirmDialog
            open={confirmingDelete}
            onOpenChange={setConfirmingDelete}
            title={`Delete ${organization.name}?`}
            description="The entry and its page go for good. Nobody else stewards it, so nobody else loses anything, but this cannot be undone."
            confirmLabel="Delete"
            destructive
            busy={remove.isPending}
            onConfirm={() => {
              setConfirmingDelete(false);
              remove.mutate(organization.id, {
                onSuccess: () => toast.success(`${organization.name} deleted`),
                onError: (err) => toast.error(err.message),
              });
            }}
          />
        </div>
      }
    />
  );
}

/** What you are to this legacy, a property of the link, so it is asked here
 *  once per legacy rather than once on the profile. */
function Relationship({ organization }: { organization: OrganizationDetail }) {
  const options = useFormChoices("relationship");
  const save = useSetRelationship(organization.id);
  const [value, setValue] = useState<string[]>(organization.relationship ?? []);
  const [other, setOther] = useState(organization.relationship_other ?? "");
  const dirty =
    value.join("|") !== (organization.relationship ?? []).join("|") ||
    other !== (organization.relationship_other ?? "");

  if (options.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-sm">Your relationship to it</p>
      <ChoiceChips
        options={options}
        value={value}
        onChange={setValue}
        other={other}
        onOtherChange={setOther}
      />
      {dirty && (
        <Button
          size="sm"
          disabled={save.isPending}
          onClick={() =>
            save.mutate(
              { relationship: value, other },
              {
                onSuccess: () => toast.success("Saved"),
                onError: (err) => toast.error(err.message),
              }
            )
          }
        >
          {save.isPending && <Spinner data-slot="icon" />}
          Save relationship
        </Button>
      )}
    </div>
  );
}

/** Ask somebody to steward this legacy, and see who has not answered yet. */
function InviteForm({
  organization,
  invites,
}: {
  organization: OrganizationDetail;
  invites: LegacyInvite[];
}) {
  const send = useSendInvite();
  const respond = useRespondToInvite();
  const [email, setEmail] = useState("");

  return (
    <div className="space-y-2 rounded-md border px-3 py-3">
      <Label htmlFor={`invite-${organization.id}`}>
        Invite a steward by email
      </Label>
      <div className="flex gap-2">
        <Input
          id={`invite-${organization.id}`}
          type="email"
          placeholder="colleague@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button
          disabled={!email.trim() || send.isPending}
          onClick={() =>
            send.mutate(
              { organizationId: organization.id, email: email.trim() },
              {
                onSuccess: () => {
                  setEmail("");
                  toast.success("Invitation sent");
                },
                onError: (err) => toast.error(err.message),
              }
            )
          }
        >
          {send.isPending && <Spinner data-slot="icon" />}
          Send
        </Button>
      </div>
      <p className="text-muted-foreground text-xs">
        They will be able to edit this entry and appear on it as a steward.
        Inviting somebody with no account is fine. The invitation waits for
        them to sign in with that address.
      </p>
      {invites.length > 0 && (
        <ul className="space-y-1 pt-1">
          {invites.map((invite) => (
            <li
              key={invite.id}
              className="flex flex-wrap items-center justify-between gap-2 text-sm"
            >
              <span className="text-muted-foreground">
                <Mail className="mr-1 inline size-3.5" />
                {invite.email}, waiting
              </span>
              <Button
                size="sm"
                variant="ghost"
                disabled={respond.isPending}
                onClick={() =>
                  respond.mutate(
                    { id: invite.id, action: "revoke" },
                    {
                      onSuccess: () => toast.success("Invitation withdrawn"),
                      onError: (err) => toast.error(err.message),
                    }
                  )
                }
              >
                Withdraw
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Invitations addressed to you. The reason somebody signs in at all, often. */
function InviteInbox({ invites }: { invites: LegacyInvite[] }) {
  const respond = useRespondToInvite();
  if (invites.length === 0) return null;

  return (
    <Card className="animate-page-enter">
      <CardHeader>
        <CardTitle className="text-base">
          {invites.length === 1
            ? "You have been invited to steward an organization"
            : `You have been invited to steward ${invites.length} organizations`}
        </CardTitle>
        <CardDescription>
          Accepting puts you on the entry as a steward, and lets you edit it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {invites.map((invite) => (
          <div
            key={invite.id}
            className="flex flex-wrap items-center gap-3 rounded-md border px-3 py-2"
          >
            <LegacyMark
              imageUrl={invite.organization.image_url}
              name={invite.organization.name}
              className="size-9"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">{invite.organization.name}</p>
              <p className="text-muted-foreground text-xs">
                {invite.invited_by || "A steward"} invited you
                {invite.grants_membership
                  ? ", and accepting carries their membership, so you would see the whole directory"
                  : ""}
              </p>
            </div>
            <div className="flex gap-2">
              <AcceptInvite
                busy={respond.isPending}
                onAccept={(relationship) =>
                  respond.mutate(
                    { id: invite.id, action: "accept", relationship },
                    {
                      onSuccess: (result) => {
                        const accepted = result as AcceptedInvite;
                        toast.success(
                          accepted.granted_membership
                            ? `You steward ${accepted.organization}, and their membership covers you`
                            : `You steward ${accepted.organization}`
                        );
                      },
                      onError: (err) => toast.error(err.message),
                    }
                  )
                }
              />
              <Button
                size="sm"
                variant="ghost"
                disabled={respond.isPending}
                onClick={() =>
                  respond.mutate(
                    { id: invite.id, action: "decline" },
                    { onError: (err) => toast.error(err.message) }
                  )
                }
              >
                Decline
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/**
 * The emailed link lands here carrying a token (`/organizations?invite=…`).
 *
 * Accepting from the link rather than the list matters: the person following
 * it may have signed up seconds ago, and the invitation is the only reason
 * they are here.
 */
/** Accepting asks the one question that belongs to the link. */
function AcceptInvite({
  busy,
  onAccept,
}: {
  busy: boolean;
  onAccept: (relationship: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const rel = useInviteRelationship();
  const options = useFormChoices("relationship");
  if (!open) {
    return (
      <Button size="sm" onClick={() => setOpen(true)}>
        Accept
      </Button>
    );
  }
  return (
    <div className="w-full space-y-3 rounded-md border p-3">
      {rel.field(options)}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={busy || !rel.ready}
          onClick={() => onAccept(rel.relationship)}
        >
          Accept
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function TokenFromEmail() {
  const [params, setParams] = useSearchParams();
  const token = params.get("invite");
  const [done, setDone] = useState(false);

  // The link used to accept on arrival. It cannot now: what you are to the
  // artist is asked before an invitation can be taken, and there is nowhere to
  // ask it in a redirect. So the token is spent on getting them here, and the
  // invitation is waiting below with its one question — the same control
  // anybody who deleted the email uses.
  useEffect(() => {
    if (!token || done) return;
    setDone(true);
    params.delete("invite");
    setParams(params, { replace: true });
    toast("Your invitation is below", {
      description: "Say what you are to the artist to accept it.",
    });
  }, [token, done]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

function NewOrganization() {
  const create = useCreateOrganization();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [website, setWebsite] = useState("");
  const [mediums, setMediums] = useState<string[]>([]);
  const [mediumsOther, setMediumsOther] = useState("");
  const [relationship, setRelationship] = useState<string[]>([]);
  const [relationshipOther, setRelationshipOther] = useState("");
  const relationshipOptions = useFormChoices("relationship");
  const mediumOptions = useFormChoices("mediums");

  if (!open) {
    return (
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Plus data-slot="icon" /> New organization
      </Button>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">New organization</CardTitle>
        <CardDescription>
          An estate, foundation or body of work gets its own directory entry.
          Only a name is required, but what you fill in here is the page other
          members will find, and what an administrator reads when reviewing it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          <Label htmlFor="new-org-name">Name *</Label>
          {/* The picker so two stewards of one estate land on one row rather
              than forking the directory. Naming one that exists no longer
              joins it — the server says so, and says to ask for an
              invitation. */}
          <OrganizationPicker id="new-org-name" value={name} onChange={setName} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="new-org-desc">Description</Label>
          <Textarea
            id="new-org-desc"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="new-org-loc">Geographic Location (City, State, Country)</Label>
            <Input
              id="new-org-loc"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-org-web">Website</Label>
            <Input
              id="new-org-web"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Mediums in which the artist works/worked</Label>
          <ChoiceChips
            options={mediumOptions}
            value={mediums}
            onChange={setMediums}
            other={mediumsOther}
            onOtherChange={setMediumsOther}
          />
        </div>
        {/* Asked when the entry is made, here as on the application: a
            question that only appears afterwards reads as one that was
            dropped. It is for the administrator reviewing this, and it is not
            published in the directory. */}
        <div className="space-y-2">
          <Label>Your relationship to the artist or entity *</Label>
          <ChoiceChips
            options={relationshipOptions}
            value={relationship}
            onChange={setRelationship}
            other={relationshipOther}
            onOtherChange={setRelationshipOther}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={
              !name.trim() || relationship.length === 0 || create.isPending
            }
            onClick={() =>
              create.mutate(
                {
                  name: name.trim(),
                  description,
                  location,
                  website,
                  mediums,
                  mediums_other: mediumsOther,
                  relationship,
                  relationship_other: relationshipOther,
                },
                {
                  onSuccess: () => {
                    setOpen(false);
                    setName("");
                    setDescription("");
                    setLocation("");
                    setWebsite("");
                    setMediums([]);
                    setMediumsOther("");
                    setRelationship([]);
                    setRelationshipOther("");
                    toast.success("Filed. An administrator will review it");
                  },
                  onError: (err) => toast.error(err.message),
                }
              )
            }
          >
            {create.isPending && <Spinner data-slot="icon" />}
            Submit
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function OrganizationEditor({ organization }: { organization: OrganizationDetail }) {
  const update = useUpdateOrganization(organization.id);
  const [name, setName] = useState(organization.name);
  const [description, setDescription] = useState(organization.description);
  const [location, setLocation] = useState(organization.location);
  const [website, setWebsite] = useState(organization.website);
  const [mediums, setMediums] = useState<string[]>(organization.mediums);
  const [mediumsOther, setMediumsOther] = useState(organization.mediums_other ?? "");
  const mediumOptions = useFormChoices("mediums");
  const setImage = useSetOrganizationImage(organization.id);
  const removeImage = useRemoveOrganizationImage(organization.id);

  const dirty =
    name !== organization.name ||
    description !== organization.description ||
    location !== organization.location ||
    website !== organization.website ||
    mediums.join(",") !== organization.mediums.join(",") ||
    mediumsOther !== (organization.mediums_other ?? "");

  return (
    <div className="space-y-3 rounded-md border px-3 py-3">
      {/* Renaming is any steward's: 422 of 430 legacies were imported with no
          creator, so a creator-only rule left nobody but an admin able to fix
          a name. The other stewards are told it happened. */}
      {organization.canRename && (
        <div className="space-y-2">
          <Label htmlFor={`name-${organization.id}`}>Name</Label>
          <Input
            id={`name-${organization.id}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor={`desc-${organization.id}`}>Description</Label>
        <Textarea
          id={`desc-${organization.id}`}
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`loc-${organization.id}`}>
            Geographic Location (City, State, Country)
          </Label>
          <Input
            id={`loc-${organization.id}`}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`web-${organization.id}`}>Website</Label>
          <Input
            id={`web-${organization.id}`}
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Picture</Label>
        <ImageUpload
          preview={
            <LegacyMark
              imageUrl={organization.image_url}
              name={organization.name}
              className="size-16"
            />
          }
          hasImage={!!organization.image_url}
          uploading={setImage.isPending}
          removing={removeImage.isPending}
          onUpload={(file) =>
            setImage.mutate(file, {
              onSuccess: () => toast.success("Picture updated"),
              onError: (err) => toast.error(err.message),
            })
          }
          onRemove={() =>
            removeImage.mutate(undefined, {
              onError: (err) => toast.error(err.message),
            })
          }
          hint="A logo or mark. It shows on the card and the page, and updates on its own."
        />
      </div>
      <div className="space-y-2">
        <Label>Mediums in which the artist works/worked</Label>
        <ChoiceChips
          options={mediumOptions}
          value={mediums}
          onChange={setMediums}
          other={mediumsOther}
          onOtherChange={setMediumsOther}
        />
        <p className="text-muted-foreground text-xs">
          The directory&apos;s medium filter is built from these.
        </p>
      </div>
      <div className="flex justify-end">
        <Button
          disabled={!dirty || update.isPending}
          onClick={() =>
            update.mutate(
              {
                ...(organization.canRename ? { name } : {}),
                description,
                location,
                website,
                mediums,
                mediums_other: mediumsOther,
              },
              {
                onSuccess: () => toast.success("Organization updated"),
                onError: (err) => toast.error(err.message),
              }
            )
          }
        >
          {update.isPending && <Spinner data-slot="icon" />}
          {update.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

/** A seeded form field's choices, so the pages and the application offer the
 *  same words — which is what makes the directory's filters work. */
function useFormChoices(field: string): string[] {
  const { data } = usePublicConfig();
  return data?.form.fields.find((f) => f.internal_name === field)?.choices ?? [];
}
